// hooks/playground/scenario/useScenarioActions.js
import { useCallback } from 'react';

export const useScenarioActions = ({
  currentScenarioId,
  currentScenario,
  editedName,
  editedDescription,
  selectedYear,
  averagingMethod,
  adjustments,
  customItems,
  hasUnsavedChanges,
  setEditMode,
  setEditedName,
  setEditedDescription,
  setIsSaving,
  onDeleteScenario,
  onLoadScenario,
  onLoadActual,
  onNewScenario,
  onSaveAsNew,
  onUpdateScenario,
}) => {
  
  // ✅ Inline edit save (from ScenarioInfoCard)
  const handleSave = useCallback(async () => {
    if (!editedName.trim()) {
      alert('Scenario name is required');
      return;
    }

    setIsSaving(true);
    
    try {
      console.log('💾 Saving inline edits:', editedName);
      
      await onUpdateScenario({
        name: editedName,
        description: editedDescription,
        averagingMethod,
        adjustments,
        customItems,
      });
      
      setEditMode(false);
      console.log('✅ Scenario saved');
    } catch (error) {
      console.error('❌ Error saving scenario:', error);
      // Error already shown in updateScenario
    } finally {
      setIsSaving(false);
    }
  }, [
    editedName,
    editedDescription,
    averagingMethod,
    adjustments,
    customItems,
    setEditMode,
    setIsSaving,
    onUpdateScenario,
  ]);

  // // ✅ Open edit modal (Edit Details button)
  // const handleEdit = useCallback(() => {
  //   console.log('✏️ Opening edit modal');
  //   if (onEdit) {
  //     onEdit(); // This opens SaveScenarioModal in 'edit' mode
  //   }
  // }, [onEdit]);

  // ✅ Open save as new modal
  const handleSaveAsNew = useCallback(() => {
    console.log('📋 Opening save as new modal');
    if (onSaveAsNew) {
      onSaveAsNew(); // This opens SaveScenarioModal in 'saveAs' mode
    }
  }, [onSaveAsNew]);

  // ✅ Delete scenario with optimistic update
  const handleDelete = useCallback(async () => {
    if (!currentScenarioId) {
      console.warn('⚠️ Cannot delete - no scenario selected');
      return;
    }

    console.log('🗑️ Deleting scenario:', currentScenarioId);
    
    try {
      const result = await onDeleteScenario();
      
      if (result?.shouldReloadBaseline) {
        console.log('✅ Scenario deleted, baseline reloaded');
      }
    } catch (error) {
      console.error('❌ Delete failed:', error);
      // Error already handled in deleteScenario
    }
  }, [currentScenarioId, onDeleteScenario]);

  // ✅ Load scenario
  const handleLoadScenarioClick = useCallback((scenarioId) => {
    console.log('📂 Loading scenario from modal:', scenarioId);
    onLoadScenario(scenarioId);
  }, [onLoadScenario]);

  // ✅ Load actual data
  const handleLoadActualClick = useCallback(() => {
    console.log('📊 Loading actual data from modal');
    onLoadActual();
  }, [onLoadActual]);

  // ✅ Create new scenario
  const handleNewScenarioClick = useCallback(() => {
    console.log('✨ Creating new scenario from modal');
    onNewScenario();
  }, [onNewScenario]);

  // ✅ Cancel inline edit
  const handleCancelEdit = useCallback(() => {
    console.log('🚫 Cancelling edit');
    setEditMode(false);
    // Reset to current scenario values
    if (currentScenario) {
      setEditedName(currentScenario.scenario_name);
      setEditedDescription(currentScenario.scenario_description || '');
    }
  }, [currentScenario, setEditMode, setEditedName, setEditedDescription]);

  return {
    handleSave,              // Inline edit save
    // handleEdit,              // Open edit modal
    handleSaveAsNew,         // Open save as new modal
    handleDelete,            // Delete with optimistic update
    handleLoadScenarioClick, // Load scenario
    handleLoadActualClick,   // Load actual data
    handleNewScenarioClick,  // New scenario
    handleCancelEdit,        // Cancel inline edit
  };
};